/**
 * AI Service
 * Makes API calls to AI providers through the backend proxy to avoid CORS issues
 */

import { getAIConfig, type AIProvider } from './ai-config';
import apiClient from '../api/client';

export interface AIMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface AICompletionOptions {
  model?: string;
  temperature?: number;
  maxTokens?: number;
  stream?: boolean;
  /** Optional AbortSignal to cancel the request (e.g. on component unmount or Stop button). */
  signal?: AbortSignal;
}

export interface AICompletionResponse {
  content: string;
  model: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export class AIServiceError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public code?: string
  ) {
    super(message);
    this.name = 'AIServiceError';
  }
}

/** User-facing message for a given error (for toasts / UI). */
export function getAIServiceErrorMessage(error: unknown): string {
  if (error instanceof AIServiceError) {
    switch (error.code) {
      case 'AI_NOT_CONFIGURED':
        return 'Configure your AI API key in Settings to use this feature.';
      case 'TIMEOUT':
        return 'Request timed out. Try a shorter prompt or try again.';
      case 'RATE_LIMIT':
        return 'Too many requests. Please wait a moment and try again.';
      case 'NETWORK_ERROR':
        return 'Network error. Check your connection and try again.';
      default:
        if (error.statusCode === 401) return 'Invalid API key. Check Settings.';
        if (error.statusCode === 429) return 'Rate limit exceeded. Please wait and try again.';
        if (error.statusCode && error.statusCode >= 500) return 'AI service temporarily unavailable. Try again later.';
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred.';
}

const AI_REQUEST_TIMEOUT_MS = 120_000;
const AI_RETRY_ATTEMPTS = 2;
const AI_RETRY_DELAY_MS = [1000, 2000];

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Get default model for provider
 */
function getDefaultModel(provider: AIProvider): string {
  switch (provider) {
    case 'anthropic':
      return 'claude-sonnet-4-20250514';
    case 'openai':
    default:
      return 'gpt-4o-mini';
  }
}

/**
 * Get available models for provider
 */
export function getAvailableModels(provider: AIProvider): Array<{ value: string; label: string; description?: string }> {
  switch (provider) {
    case 'openai':
      return [
        { value: 'gpt-4o', label: 'GPT-4o', description: 'Most capable, latest model' },
        { value: 'gpt-4o-mini', label: 'GPT-4o Mini', description: 'Fast and affordable' },
        { value: 'gpt-4-turbo', label: 'GPT-4 Turbo', description: 'Previous generation' },
        { value: 'gpt-4', label: 'GPT-4', description: 'Standard GPT-4' },
        { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo', description: 'Fast and cost-effective' }
      ];
    case 'anthropic':
      // Only Sonnet 4 and Claude 3 Haiku are confirmed working with our proxy/API
      return [
        { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4', description: 'Most capable, latest' },
        { value: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku', description: 'Fast and affordable' }
      ];
    default:
      return [];
  }
}

function normalizeAIError(error: any): AIServiceError {
  if (error instanceof AIServiceError) return error;
  if (error.response) {
    const status = error.response.status;
    const code = status === 429 ? 'RATE_LIMIT' : (error.response.data?.code || 'API_ERROR');
    const message = error.response.data?.error || error.response.data?.message || `API request failed: ${error.response.statusText}`;
    return new AIServiceError(message, status, code);
  }
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return new AIServiceError(
      'Request timed out. The AI response is taking longer than expected. Try reducing the prompt size or try again.',
      504,
      'TIMEOUT'
    );
  }
  if (error.code === 'ERR_CANCELED' || error.name === 'AbortError') {
    return new AIServiceError('Request was cancelled.', undefined, 'CANCELLED');
  }
  return new AIServiceError(
    error instanceof Error ? error.message : 'Unknown error occurred',
    undefined,
    'NETWORK_ERROR'
  );
}

/**
 * Complete a chat conversation with AI
 * Uses backend proxy to avoid CORS issues. Retries on network/5xx/429 with backoff. Supports AbortSignal.
 */
export async function completeChat(
  messages: AIMessage[],
  options: AICompletionOptions = {}
): Promise<AICompletionResponse> {
  const config = getAIConfig();
  if (!config) {
    throw new AIServiceError(
      'AI is not configured. Please set your API key in Settings.',
      undefined,
      'AI_NOT_CONFIGURED'
    );
  }

  const { apiKey, provider, model: savedModel } = config;
  let model = options.model || savedModel || getDefaultModel(provider);
  const available = getAvailableModels(provider);
  const isValidModel = available.some((m) => m.value === model);
  if (!isValidModel) model = getDefaultModel(provider);
  const { signal } = options;

  const doRequest = async (): Promise<AICompletionResponse> => {
    const response = await apiClient.post(
      '/ai/chat',
      {
        provider,
        apiKey,
        messages,
        model,
        temperature: options.temperature,
        maxTokens: options.maxTokens,
        stream: options.stream,
      },
      {
        timeout: AI_REQUEST_TIMEOUT_MS,
        signal,
      }
    );
    return response.data as AICompletionResponse;
  };

  let lastError: AIServiceError | null = null;
  for (let attempt = 0; attempt <= AI_RETRY_ATTEMPTS; attempt++) {
    try {
      return await doRequest();
    } catch (error: any) {
      lastError = normalizeAIError(error);
      if (lastError.code === 'CANCELLED' || lastError.code === 'AI_NOT_CONFIGURED') throw lastError;
      const isRetryable =
        lastError.code === 'NETWORK_ERROR' ||
        lastError.code === 'TIMEOUT' ||
        lastError.code === 'RATE_LIMIT' ||
        (lastError.statusCode != null && lastError.statusCode >= 500);
      if (!isRetryable || attempt === AI_RETRY_ATTEMPTS) throw lastError;
      await delay(AI_RETRY_DELAY_MS[attempt]);
    }
  }
  throw lastError ?? new AIServiceError('Unknown error', undefined, 'NETWORK_ERROR');
}

/**
 * Simple completion (single user message)
 */
export async function complete(
  prompt: string,
  systemPrompt?: string,
  options: AICompletionOptions = {}
): Promise<string> {
  const messages: AIMessage[] = [];
  
  if (systemPrompt) {
    messages.push({ role: 'system', content: systemPrompt });
  }
  
  messages.push({ role: 'user', content: prompt });
  
  const response = await completeChat(messages, options);
  return response.content;
}
